import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { MicIcon, RadarIcon, SendIcon, SquareIcon, WaveIcon } from './icons';
import { Button } from './Button';
import { SITUATION_LABEL, SITUATION_QUESTION } from '../utils/quickActions.js';
import './ChatInput.css';

// Boutons Envoyer et Arrêter : composant Button animé (léger agrandissement au survol)
const MotionButton = motion.create(Button);

/**
 * Compositeur. `effortSelector` et `modelSelector` : contrôles du pied (sprint 6 : profondeur
 * de réflexion à gauche du modèle). `onSituation` : puce « Point de situation » (CU-105, même
 * nom que la tuile d'accueil), affichée au-dessus du champ quand `showSituation` est vrai et
 * masquée pendant une réponse ; `effortSupported` faux : pas de mention « analyse approfondie »
 * (modèle à profondeur fixe).
 * Hors ligne (UI-11) : `offline` bloque l'envoi (bouton inactif, Entrée sans effet, texte
 * gardé dans le champ) ; `banner` (bandeau de connexion) s'affiche au-dessus du champ.
 */
export function ChatInput({
  onSend, isLoading, onStop, modelSelector = null, effortSelector = null, onSituation = null, showSituation = false, effortSupported = true,
  offline = false, banner = null,
}) {
  const [input, setInput] = useState('');
  // MotionConfig « user » ne coupe pas les boucles d'opacité : halo figé si mouvement réduit
  const reduceMotion = useReducedMotion();
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [interimText, setInterimText] = useState('');

  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef(''); // texte confirmé avant/pendant l'écoute

  // ── Support Web Speech API ───────────────────────────────────────
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SR);
  }, []);

  // ── Auto-resize textarea ─────────────────────────────────────────
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  // ── Arrêter la reconnaissance ────────────────────────────────────
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null; // désactiver le redémarrage auto
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimText('');
  }, []);

  // ── Démarrer la reconnaissance ───────────────────────────────────
  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    // Mémoriser le texte déjà dans le champ
    baseTextRef.current = input;

    const recognition = new SR();
    recognitionRef.current = recognition;

    recognition.lang = 'fr-FR';
    recognition.continuous = true;     // écoute jusqu'au clic stop
    recognition.interimResults = true; // affichage temps réel

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      let finalChunk = '';
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) {
          finalChunk += r[0].transcript;
        } else {
          interim += r[0].transcript;
        }
      }

      // Ajouter le texte final confirmé à la base
      if (finalChunk) {
        const sep = baseTextRef.current && !baseTextRef.current.endsWith(' ') ? ' ' : '';
        baseTextRef.current = baseTextRef.current + sep + finalChunk;
      }

      // Afficher : base + interim (interim apparaît "en cours")
      const sep = baseTextRef.current && interim && !baseTextRef.current.endsWith(' ') ? ' ' : '';
      setInput(baseTextRef.current + (interim ? sep + interim : ''));
      setInterimText(interim);
    };

    recognition.onerror = (event) => {
      // no-speech = silence normal → on continue
      if (event.error === 'no-speech') return;
      console.error('Speech error:', event.error);
      stopListening();
    };

    // Redémarrage automatique si le navigateur coupe (timeout ~1min Chrome)
    recognition.onend = () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.start(); } catch { /* déjà démarré */ }
      } else {
        setIsListening(false);
        setInterimText('');
      }
    };

    recognition.start();
  }, [input, stopListening]);

  // ── Clic micro ───────────────────────────────────────────────────
  const handleMicClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // ── Modification manuelle pendant l'écoute ───────────────────────
  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (isListening) {
      // Synchroniser baseTextRef avec la frappe manuelle
      baseTextRef.current = e.target.value;
    }
  };

  // ── Envoi ────────────────────────────────────────────────────────
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isListening) stopListening(); // arrêter la dictée avant d'envoyer
    // Hors ligne : rien n'est envoyé, la question reste dans le champ
    if (input.trim() && !isLoading && !offline) {
      onSend(input);
      setInput('');
      baseTextRef.current = '';
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
    if (e.key === 'Escape' && isListening) {
      stopListening();
    }
  };

  // ── Nettoyage ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      }
    };
  }, []);

  return (
    <motion.div
      className="chat-input-container"
      initial={{ y: 12, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.36, delay: 0.1, ease: [0, 0, 0, 1] }}
    >
      <div className="chat-input-wrapper">
        {/* ── Bandeau de connexion (hors ligne, reprise) ── */}
        {banner}
        <form onSubmit={handleSubmit} className="chat-input-form">

          {/* ── Action rapide : point de situation (question en langage naturel) ── */}
          {onSituation && showSituation && !isLoading && !offline && (
            <div className="composer-quick">
              <button
                type="button"
                className="composer-quick__chip"
                onClick={onSituation}
                aria-label={`${SITUATION_LABEL} : demander à l'assistant ce qui est anormal à surveiller en ce moment${effortSupported ? ' (analyse approfondie)' : ''}`}
                title={`${SITUATION_QUESTION}${effortSupported ? ' (analyse approfondie)' : ''}`}
              >
                <RadarIcon size={14} />
                <span>{SITUATION_LABEL}</span>
              </button>
            </div>
          )}

          <div className={`input-group ${isListening ? 'input-group--listening' : ''}`}>

            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                isListening
                  ? 'Parlez, le texte apparaît ici…'
                  : 'Posez une question sur votre flotte…'
              }
              className="message-input"
              aria-label="Votre question"
              disabled={isLoading}
              rows={1}
            />

            {/* ── Bouton Micro ───────────────────────────────────── */}
            {speechSupported && !isLoading && (
              <Button
                variant="ghost"
                iconOnly
                onClick={handleMicClick}
                className={`btn-mic ${isListening ? 'btn-mic--active' : ''}`}
                title={isListening ? 'Arrêter la dictée (Échap)' : 'Dicter votre question'}
                aria-label={isListening ? 'Arrêter la dictée, enregistrement en cours' : 'Dicter votre question'}
                aria-pressed={isListening}
              >
                <AnimatePresence mode="wait">
                  {isListening ? (
                    <motion.span
                      key="wave"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                    >
                      <WaveIcon size={20} strokeWidth={2.25} />
                    </motion.span>
                  ) : (
                    <motion.span
                      key="mic"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.15 }}
                    >
                      <MicIcon size={20} />
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Halo pulsant (image fixe si l'utilisateur réduit les animations, cahier §3.6) */}
                {isListening && reduceMotion && (
                  <span className="btn-mic__pulse btn-mic__pulse--static" aria-hidden="true" />
                )}
                {isListening && !reduceMotion && (
                  <motion.span
                    className="btn-mic__pulse"
                    animate={{ scale: [1, 1.9, 1], opacity: [0.5, 0, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
              </Button>
            )}

            {/* ── Bouton Stop IA / Envoyer ───────────────────────── */}
            {isLoading ? (
              <MotionButton
                variant="danger"
                iconOnly
                onClick={onStop}
                className="btn-stop"
                aria-label="Arrêter la réponse"
                title="Arrêter la réponse"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SquareIcon size={16} className="btn-stop__icon" />
              </MotionButton>
            ) : (
              <MotionButton
                type="submit"
                variant="primary"
                iconOnly
                disabled={!input.trim() || isLoading || offline}
                className="btn-send"
                aria-label="Envoyer la question"
                title={offline ? 'Hors ligne : envoi impossible pour le moment' : 'Envoyer (Entrée)'}
                whileHover={{ scale: input.trim() && !offline ? 1.05 : 1 }}
                whileTap={{ scale: input.trim() && !offline ? 0.95 : 1 }}
              >
                <SendIcon size={18} strokeWidth={2.25} />
              </MotionButton>
            )}

          </div>

          {/* ── Footer ──────────────────────────────────────────── */}
          <div className="input-footer">
            <span className="input-hint">
              {isListening ? (
                <span className="input-hint--listening">
                  <span className="input-hint__rec" aria-hidden="true" />
                  Dictée active
                  {interimText && (
                    <span className="input-hint--interim"> — en cours : « {interimText} »</span>
                  )}
                  {' '}· micro ou <kbd>Échap</kbd> pour arrêter
                </span>
              ) : offline ? (
                'Hors ligne : envoi suspendu jusqu’au retour du réseau'
              ) : (
                <>
                  <kbd>Entrée</kbd> pour envoyer ·{' '}
                  <kbd>Maj + Entrée</kbd> pour aller à la ligne
                </>
              )}
            </span>
            {(effortSelector || modelSelector) && (
              <div className="composer-controls">
                {effortSelector}
                {modelSelector}
              </div>
            )}
          </div>

        </form>
      </div>
    </motion.div>
  );
}